import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  calculatePricing,
  PricingCalculation,
  validateParcelCount,
} from '@/utils/entities/city/montana';
import { MONTANA_CONSTANTS } from '@/utils/entities/city/montana';

export interface UsePricingCalculatorReturn {
  parcels: string;
  setParcels: (parcels: string) => void;
  pricing: PricingCalculation;
  isValidParcels: boolean;
  selectedTier: 'silver' | 'gold' | 'custom' | null;
  setSelectedTier: (tier: 'silver' | 'gold' | 'custom') => void;
  resetCalculator: () => void;
  shareableUrl: string;
}

export const usePricingCalculator = (initialParcels?: number): UsePricingCalculatorReturn => {
  const params = useParams();
  const [parcels, setParcelsState] = useState<string>(
    (initialParcels || MONTANA_CONSTANTS.DEFAULT_PARCELS).toLocaleString(),
  );
  const [selectedTier, setSelectedTier] = useState<'silver' | 'gold' | 'custom' | null>(null);

  const pricing = calculatePricing(parseInt(parcels.replace(/,/g, '')) || 0);
  const isValidParcels =
    parcels === '' ? false : validateParcelCount(parseInt(parcels.replace(/,/g, '')) || 0);

  // Helper function to extract parcel number from route param (handles "406-parcels" format)
  const extractParcelsFromRoute = (routeParcels: string | undefined): number | null => {
    if (!routeParcels) return null;

    // Handle both "406" and "406-parcels" formats
    const match = routeParcels.match(/^(\d+)(?:-parcels)?$/);
    if (match) {
      const parsed = parseInt(match[1], 10);
      return validateParcelCount(parsed) ? parsed : null;
    }
    return null;
  };

  const setParcels = useCallback((newParcels: string) => {
    setParcelsState(newParcels);
  }, []);

  const resetCalculator = useCallback(() => {
    setParcelsState(MONTANA_CONSTANTS.DEFAULT_PARCELS.toLocaleString());
    setSelectedTier(null);
  }, []);

  // Create shareable URL (prefer path-based for cleaner URLs)
  const shareableUrl = `${window.location.origin}/pricing/montana/${parcels.replace(/,/g, '')}-parcels${
    selectedTier ? `?tier=${selectedTier}` : ''
  }`;

  // Update URL when parcels or tier changes (use path-based routing)
  useEffect(() => {
    const currentPath = window.location.pathname;
    const expectedPath = `/pricing/montana/${parcels.replace(/,/g, '')}-parcels`;

    if (currentPath !== expectedPath) {
      const newUrl = `${window.location.origin}${expectedPath}`;
      const url = new URL(newUrl);

      if (selectedTier) {
        url.searchParams.set('tier', selectedTier);
      }

      window.history.replaceState({}, '', url.toString());
    }
  }, [parcels, selectedTier]);

  // Initialize from URL parameters (supports both route params and query params)
  useEffect(() => {
    const routeParcels = extractParcelsFromRoute(params.parcels);
    const urlParams = new URLSearchParams(window.location.search);
    const queryParcels = urlParams.get('parcels');
    const urlTier = urlParams.get('tier') as 'silver' | 'gold' | 'custom' | null;

    // Priority: route params > query params > default
    let finalParcels: string = MONTANA_CONSTANTS.DEFAULT_PARCELS.toLocaleString();

    if (routeParcels !== null) {
      finalParcels = routeParcels.toLocaleString();
    } else if (queryParcels) {
      const parsedParcels = parseInt(queryParcels, 10);
      if (validateParcelCount(parsedParcels)) {
        finalParcels = parsedParcels.toLocaleString();
      }
    }

    setParcelsState(finalParcels);

    if (urlTier && ['silver', 'gold', 'custom'].includes(urlTier)) {
      setSelectedTier(urlTier);
    }
  }, [params.parcels]);

  return {
    parcels,
    setParcels,
    pricing,
    isValidParcels,
    selectedTier,
    setSelectedTier,
    resetCalculator,
    shareableUrl,
  };
};
